import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(229, 69, 69, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #e54545;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #e54545;
  border-bottom: 1px solid rgba(229, 69, 69, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #e54545;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(229, 69, 69, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(229, 69, 69, 0.5);
  }
`;

export const CourageMetadata = {
  title: "The Courage to Be Disliked",
  description: "How to Free Yourself, Change your Life and Achieve Real Happiness",
  tags: ['Philosophy', 'Psychology', 'Self-Improvement'],
  author: "Ichiro Kishimi and Fumitake Koga",
  datePublished: "2025-03-31",
  category: "Book Review",
  path: "/book-review/the-courage-to-be-disliked",
  coverImage: "/images/the_courage_to_be_disliked.jpg",
};

const CourageToBeDisliked = () => {
  const navigate = useNavigate();
  const metadata = CourageMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
        {metadata.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            The Courage to Be Disliked is a philosophical dialogue that brings the teachings of Alfred Adler to life through an engaging conversation between a philosopher and a young man. The book challenges the reader to rethink long-held beliefs about happiness, trauma, and interpersonal relationships.
          </p>
          <br />
          <p>
            Unlike traditional self-help books, this one dismantles the idea that our past determines our future. It promotes the empowering view that we can change our lives in any moment, simply by choosing to live differently. Its core messages revolve around freedom, detachment, and the pursuit of a meaningful life over one defined by validation.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem>Separation of Tasks: Understand what is and isn't your responsibility in relationships.</ListItem>
            <ListItem>Freedom vs. Approval: True freedom means letting go of the need for others’ recognition.</ListItem>
            <ListItem>Trauma Denial: The past does not determine the present. We live in the here and now.</ListItem>
            <ListItem>Living for Contribution: Life gains meaning when we contribute to others’ well-being.</ListItem>
            <ListItem>Interpersonal Relationships: All problems are interpersonal relationship problems.</ListItem>
            <ListItem>Self-Acceptance: Accepting yourself as you are, instead of striving to be "special."</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>“You are not the center of the world, and neither am I. That’s precisely why we are free.”</Quote>
          <Quote>“All problems are interpersonal relationship problems.”</Quote>
          <Quote>“You can be happy now. Not someday. Not when you’ve achieved something. Now.”</Quote>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            This book was an enjoyable and thought-provoking read. While some of the ideas initially felt jarring, particularly the rejection of trauma as a life-determining force, they became more compelling the deeper I got into Adler's logic. The perspective that we are not bound by our past but can choose how to live going forward is both practical and surprisingly optimistic.
          </p>
          <br />
          <p>
            The dialogue format made the content very accessible. It’s conversational, even playful at times, which made the heavier concepts easier to absorb. It's also a gentle introduction to Adlerian psychology, without the dryness you might expect from traditional philosophy or psychology texts.
          </p>
          <br />
          <p>
            One idea that didn’t sit fully right with me was the emphasis on completely letting go of the desire for recognition. While it’s true that living for others’ approval can be limiting, the notion that seeking any form of validation is inherently misguided felt a bit extreme. Still, the book invites you to think critically, rather than take everything at face value—and that’s part of its appeal. I found myself more engaged with the practical takeaways about how we relate to others and manage our own expectations than with the psychological theory behind it all.
            </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default CourageToBeDisliked;
